import { useCallback, useEffect, useMemo, useState } from "react";
import PageTitle from "../../../components/ui/PageTitle";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../components/ui/ErrorMessage";
import Button from "../../../components/ui/Button";
import {
  GovernancePermission,
  GovernanceRole,
  getAllGovernancePermissions,
  getAllGovernanceRoles,
  updateGovernanceRolePermissions,
} from "../../../lib/apiClient";
import { getErrorMessage } from "../../../lib/utils";
import toast from "react-hot-toast";

const roleSortOrder = ["SuperAdmin", "Admin", "Sales", "Support", "User"];

const sortRoles = (roles: GovernanceRole[]) => {
  return [...roles].sort((a, b) => {
    const aIdx = roleSortOrder.indexOf(a.name);
    const bIdx = roleSortOrder.indexOf(b.name);
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return a.name.localeCompare(b.name);
  });
};

const getRolePermissionIds = (role: GovernanceRole | null): Set<string> => {
  if (!role) return new Set<string>();
  return new Set(role.permissions.map((permission) => permission._id));
};

export default function SuperAdminRolesPage() {
  const [roles, setRoles] = useState<GovernanceRole[]>([]);
  const [permissions, setPermissions] = useState<GovernancePermission[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [search, setSearch] = useState("");
  const [draftPermissionIds, setDraftPermissionIds] = useState<Set<string>>(
    new Set<string>()
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedRole = useMemo(
    () => roles.find((role) => role._id === selectedRoleId) || null,
    [roles, selectedRoleId]
  );

  const serverPermissionIds = useMemo(
    () => getRolePermissionIds(selectedRole),
    [selectedRole]
  );

  const isSuperAdminRole = selectedRole?.name === "SuperAdmin";

  const hasChanges = useMemo(() => {
    if (!selectedRole) return false;
    if (serverPermissionIds.size !== draftPermissionIds.size) return true;
    for (const id of Array.from(serverPermissionIds)) {
      if (!draftPermissionIds.has(id)) return true;
    }
    return false;
  }, [selectedRole, serverPermissionIds, draftPermissionIds]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [roleData, permissionData] = await Promise.all([
        getAllGovernanceRoles(),
        getAllGovernancePermissions(),
      ]);

      const sortedRoles = sortRoles(roleData);
      setRoles(sortedRoles);
      setPermissions(permissionData);

      if (sortedRoles.length > 0) {
        const firstRoleId = sortedRoles[0]._id;
        setSelectedRoleId(firstRoleId);
        setDraftPermissionIds(getRolePermissionIds(sortedRoles[0]));
      }
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(`Failed to load role permissions: ${message}`, {
        id: "role-permissions-load-error",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setDraftPermissionIds(getRolePermissionIds(selectedRole));
  }, [selectedRoleId, selectedRole]);

  const filteredPermissions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return permissions;

    return permissions.filter((permission) => {
      const key = `${permission.resource}:${permission.action}`.toLowerCase();
      const description = (permission.description || "").toLowerCase();
      return (
        key.includes(query) ||
        permission.resource.toLowerCase().includes(query) ||
        permission.action.toLowerCase().includes(query) ||
        description.includes(query)
      );
    });
  }, [permissions, search]);

  const groupedPermissions = useMemo(() => {
    const groups = new Map<string, GovernancePermission[]>();

    filteredPermissions.forEach((permission) => {
      const list = groups.get(permission.resource) || [];
      list.push(permission);
      groups.set(permission.resource, list);
    });

    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([resource, list]) => ({
        resource,
        permissions: list.sort((a, b) => a.action.localeCompare(b.action)),
      }));
  }, [filteredPermissions]);

  const togglePermission = (permissionId: string) => {
    if (isSuperAdminRole) return;

    setDraftPermissionIds((prev) => {
      const next = new Set(prev);
      if (next.has(permissionId)) next.delete(permissionId);
      else next.add(permissionId);
      return next;
    });
  };

  const selectRole = (roleId: string) => {
    setSelectedRoleId(roleId);
  };

  const savePermissions = async () => {
    if (!selectedRole || isSuperAdminRole) return;

    setIsSaving(true);
    setError(null);

    try {
      const updatedRole = await updateGovernanceRolePermissions(
        selectedRole._id,
        Array.from(draftPermissionIds)
      );

      setRoles((prev) =>
        sortRoles(
          prev.map((role) => (role._id === updatedRole._id ? updatedRole : role))
        )
      );
      toast.success(`Permissions updated for ${selectedRole.name}.`, {
        id: "role-permissions-save-success",
      });
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(`Failed to save role permissions: ${message}`, {
        id: "role-permissions-save-error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageTitle
          title="Role Permissions"
          subtitle="Manage role-level access for core and custom roles"
        />
        <LoadingIndicator text="Loading role governance..." size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Role Permissions"
        subtitle="Search permissions, review by resource, and update role assignments"
      />

      {error && <ErrorMessage title="Unable to Continue" message={error} />}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        <aside className="xl:col-span-3 bg-dark-secondary rounded-xl border border-dark-tertiary p-4 space-y-3 h-fit">
          <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wide">
            Roles
          </h2>
          <div className="space-y-2">
            {roles.map((role) => (
              <button
                key={role._id}
                type="button"
                onClick={() => selectRole(role._id)}
                className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition-colors ${
                  selectedRoleId === role._id
                    ? "border-functional-info bg-functional-info/10 text-text-primary"
                    : "border-dark-tertiary bg-dark-primary/70 text-text-secondary hover:text-text-primary"
                }`}
              >
                <div className="font-medium">{role.name}</div>
                <div className="text-xs opacity-80">
                  {(role.permissions || []).length} permissions
                </div>
              </button>
            ))}
          </div>
        </aside>

        <section className="xl:col-span-9 bg-dark-secondary rounded-xl border border-dark-tertiary p-4 md:p-5 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3 lg:justify-between">
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-text-primary">
                {selectedRole ? selectedRole.name : "Select a Role"}
              </h3>
              <p className="text-sm text-text-secondary">
                {selectedRole?.description || "No description available."}
              </p>
              {isSuperAdminRole && (
                <p className="text-xs text-functional-warning">
                  SuperAdmin permissions are protected and cannot be changed.
                </p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search permissions..."
                className="w-full sm:w-72 bg-dark-primary border border-dark-border rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-functional-info/50"
              />
              <Button
                onClick={savePermissions}
                isLoading={isSaving}
                disabled={!selectedRole || isSuperAdminRole || !hasChanges || isSaving}
              >
                Save Changes
              </Button>
            </div>
          </div>

          {groupedPermissions.length === 0 ? (
            <div className="rounded-lg border border-dark-tertiary bg-dark-primary/60 p-5 text-sm text-text-secondary">
              No permissions match the current search.
            </div>
          ) : (
            <div className="space-y-4">
              {groupedPermissions.map((group) => (
                <div
                  key={group.resource}
                  className="rounded-xl border border-dark-tertiary bg-dark-primary/60 p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-text-primary">
                      {group.resource}
                    </h4>
                    <span className="text-xs text-text-secondary">
                      {group.permissions.length} actions
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {group.permissions.map((permission) => {
                      const checked = draftPermissionIds.has(permission._id);
                      return (
                        <label
                          key={permission._id}
                          className={`rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                            checked
                              ? "border-functional-success/60 bg-functional-success/10"
                              : "border-dark-tertiary bg-dark-secondary/70"
                          } ${isSuperAdminRole ? "cursor-not-allowed opacity-70" : ""}`}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => togglePermission(permission._id)}
                              disabled={isSuperAdminRole}
                              className="mt-1"
                            />
                            <div>
                              <div className="text-sm font-medium text-text-primary">
                                {permission.resource}:{permission.action}
                              </div>
                              <div className="text-xs text-text-secondary mt-1">
                                {permission.description || "No description"}
                              </div>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
